import { useEffect, useMemo, useState } from 'react';
import { BEATS, Cell, FERMATAS, Setting, cells, passionSettings } from '../../data/chorales/passion';
import { chordName } from '../../data/chorales/derive';
import { keyName } from '../../music/key';
import { barAt } from '../../music/piece';
import { midiToPitch, pcName } from '../../music/pitch';
import { formatRoman } from '../../music/roman';
import type { PlayNote } from '../../audio/player';
import { usePlayer, usePrefs, useWidth } from '../hooks';
import { CORPUS } from '../../data/chorales/corpusIndex';

const PASSION_TUNE = 'befiehl-du-deine-wege-melody-1';
import { PlayIcon } from '../components/Icons';
import { PlayButton } from '../components/PlayButton';
import { Crumbs } from '../components/Shell';

interface Segment {
  setting: Setting;
  start: number;
  end: number;
  offset: number;
}

const LINES = [
  [0, 7],
  [7, 13],
  [13, 23],
  [23, 29],
  [29, 39],
  [39, 45],
];

function excerpt(s: Setting, from: number, to: number, offset: number, samePitch: boolean): PlayNote[] {
  const shift = samePitch ? s.semis : 0;
  return s.piece.notes
    .filter((n) => n.onset < to - 1e-6 && n.onset + n.dur > from + 1e-6)
    .map((n) => {
      const a = Math.max(n.onset, from);
      const b = Math.min(n.onset + n.dur, to);
      return { onset: offset + a - from, dur: b - a, midi: n.midi + shift, line: n.line, velocity: n.line === 3 ? 0.8 : n.line === 0 ? 0.78 : 0.6 };
    });
}

function tensionColor(v: number): string {
  const pct = Math.round(Math.min(1, Math.max(0, v)) * 100);
  return `color-mix(in oklab, var(--tension) ${pct}%, var(--rest))`;
}

function feelingWord(v: number): string {
  return v < 0.15 ? 'at rest' : v < 0.4 ? 'leaning' : v < 0.7 ? 'pulling' : 'urgent';
}

export function Passion() {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player, position, playing } = usePlayer();
  const { passion, elsewhere } = useMemo(() => passionSettings(), []);
  const [showElsewhere, setShowElsewhere] = useState(false);
  const [samePitch, setSamePitch] = useState(true);
  // Open on the beat where the five settings differ most: the clearest place to hear the point of the room.
  const [beat, setBeat] = useState(() => {
    let best = 0;
    let most = -1;
    const grids = passion.map((x) => cells(x, BEATS));
    for (let t = 0; t < BEATS; t++) {
      const n = new Set(grids.map((g) => chordName(g[t].chord))).size;
      if (n > most) {
        most = n;
        best = t;
      }
    }
    return best;
  });
  const [segments, setSegments] = useState<Segment[]>([]);
  const [stripRef, stripWidth] = useWidth<HTMLDivElement>();
  useEffect(() => player.setTempo(prefs.tempo), [player, prefs.tempo]);

  const rows = useMemo(() => (showElsewhere ? [...passion, ...elsewhere] : passion), [showElsewhere, passion, elsewhere]);
  const grid = useMemo(() => new Map(rows.map((s) => [s.id, cells(s, BEATS)])), [rows]);

  const play = (segs: Array<{ setting: Setting; start: number; end: number }>, gap = 1) => {
    let t = 0;
    const out: Segment[] = [];
    const notes: PlayNote[] = [];
    for (const s of segs) {
      notes.push(...excerpt(s.setting, s.start, s.end, t, samePitch));
      out.push({ ...s, offset: t });
      t += s.end - s.start + gap;
    }
    setSegments(out);
    player.setNotes(notes, t - gap);
    void player.play(0);
  };

  const current = playing ? [...segments].reverse().find((s) => position >= s.offset - 1e-6) : undefined;
  const playingBeat = current ? Math.floor(current.start + (position - current.offset)) : -1;

  const cellW = Math.max(6, (stripWidth || 600) / BEATS);
  const melodyRef = passion[0];
  const melody = melodyRef ? melodyRef.melody.map((m) => ({ ...m, midi: m.midi + melodyRef.semis })) : [];
  const lo = Math.min(...melody.map((m) => m.midi)) - 1;
  const hi = Math.max(...melody.map((m) => m.midi)) + 1;
  const melH = 64;

  const selected = rows.map((s) => ({ s, c: grid.get(s.id)![beat] }));
  const melodyNote = selected[0]?.c.melodyMidi;

  return (
    <div className="passion">
      <Crumbs items={[{ href: '#compare', label: 'Compare' }, { href: '#tunes', label: 'Same tune, new harmony' }, { label: 'The Passion Chorale' }]} />
      <header className="page-head">
        <span className="label">Same tune, new harmony</span>
        <h1>One melody, five harmonies</h1>
        <p>
          The Passion Chorale returns five times in Bach’s St Matthew Passion, each time with new words and, four times out of five, a new harmony. Pick a beat to hear
          the same melody note in every setting.
        </p>
        <p className="hint">
          Colour shows the pull of the harmony: cool at rest, warm when it strains.{' '}
          <a href={`#tune.${PASSION_TUNE}`}>All {CORPUS.tunes.find((t) => t.slug === PASSION_TUNE)?.ids.length ?? ''} of Bach’s settings of this tune →</a>
        </p>
      </header>

      <div className="transport" style={{ marginBottom: 12 }}>
        <PlayButton
          playing={playing}
          label={`Hear beat ${beat + 1} in all ${rows.length} settings`}
          onPause={() => player.pause()}
          onPlay={() => play(rows.map((s) => ({ setting: s, start: beat, end: Math.min(BEATS, beat + 1.5) })), 0.5)}
        />
        {current && <span className="where">Now: {current.setting.incipit}</span>}
        <span className="field">Whole phrase:</span>
        <div className="seg" role="group" aria-label="Hear a whole phrase in every setting">
          {LINES.map(([a, b], i) => (
            <button key={i} onClick={() => play(rows.map((s) => ({ setting: s, start: a, end: b + 1 })), 1)} title={`Hear phrase ${i + 1} in every setting`} aria-label={`Phrase ${i + 1}`}>
              {i + 1}
            </button>
          ))}
        </div>
        <span className="grow" />
        <label className="field">
          <input id="same-pitch" type="checkbox" checked={samePitch} onChange={(e) => setSamePitch(e.target.checked)} /> Same pitch for all
        </label>
        <label className="field">
          <input id="elsewhere" type="checkbox" checked={showElsewhere} onChange={(e) => setShowElsewhere(e.target.checked)} /> Also elsewhere in Bach
        </label>
      </div>

      <div className="panel grid-scroll" style={{ padding: '14px 16px', ['--cols' as string]: BEATS }}>
        <div className="passion-row">
          <div className="passion-label">
            <span className="label">The melody</span>
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              Herzlich tut mich verlangen (H. L. Hassler)
            </span>
          </div>
          <div ref={stripRef} className="passion-strip">
            {stripWidth > 0 && (
              <svg width={stripWidth} height={melH} viewBox={`0 0 ${stripWidth} ${melH}`} role="img" aria-label="The shared melody">
                {LINES.map(([a], i) => (
                  <line key={i} x1={a * cellW} x2={a * cellW} y1={0} y2={melH} className="s-grid" />
                ))}
                {melody.map((m, i) => (
                  <g key={i}>
                    <line
                      x1={m.onset * cellW + 1}
                      x2={(m.onset + m.dur) * cellW - 1}
                      y1={6 + ((hi - m.midi) / (hi - lo)) * (melH - 16)}
                      y2={6 + ((hi - m.midi) / (hi - lo)) * (melH - 16)}
                      className={m.onset <= beat + 1e-6 && m.onset + m.dur > beat + 1e-6 ? 's-tension' : 's-ink'}
                      strokeWidth={3}
                      strokeLinecap="round"
                    />
                    {m.fermata && (
                      <text x={m.onset * cellW + 2} y={melH - 2} className="t-mono">
                        𝄐
                      </text>
                    )}
                  </g>
                ))}
              </svg>
            )}
          </div>
        </div>

        {rows.map((s, ri) => {
          const cs = grid.get(s.id)!;
          const isPlayingRow = current?.setting.id === s.id;
          return (
            <div className="passion-row" key={s.id} data-playing={isPlayingRow} style={ri === passion.length ? { borderTop: '1px dashed var(--rule-strong)', paddingTop: 8, marginTop: 4 } : undefined}>
              <div className="passion-label">
                <button className="btn quiet" style={{ height: 30, padding: '0 8px', marginLeft: -8 }} onClick={() => play([{ setting: s, start: 0, end: BEATS }])} aria-label={`Play ${s.incipit}`}>
                  <PlayIcon />
                </button>
                <div>
                  <a href={`#${s.piece.id}`} style={{ color: 'var(--ink)', textDecoration: 'none', fontFamily: 'var(--display)', fontSize: '1.02rem', lineHeight: 1.2, display: 'block' }}>
                    {s.incipit}
                  </a>
                  <span className="muted" style={{ fontSize: '0.76rem' }}>
                    {s.piece.catalogue} · {keyName(s.view.events[s.view.events.length - 1].key)}
                    {s.piece.id === 'bwv244-17' ? ' · derived' : ''}
                  </span>
                </div>
              </div>
              <div className="passion-strip">
                <svg width={cellW * BEATS} height={30} viewBox={`0 0 ${cellW * BEATS} 30`} role="img" aria-label={`Harmony of ${s.incipit}, beat by beat`}>
                  {cs.map((c) => (
                    <g
                      key={c.t}
                      onClick={() => {
                        setBeat(c.t);
                        play(rows.map((x) => ({ setting: x, start: c.t, end: Math.min(BEATS, c.t + 1.5) })), 0.5);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <title>{`Beat ${c.t + 1}: ${chordName(c.chord)} (${formatRoman(c.event.spec.rn)} in ${keyName(c.key)})`}</title>
                      <rect
                        x={c.t * cellW + 0.5}
                        y={2}
                        width={cellW - 1}
                        height={26}
                        rx={2}
                        style={{ fill: tensionColor(c.tension), opacity: 0.35 + c.tension * 0.55 }}
                        stroke={c.t === beat ? 'var(--ink)' : isPlayingRow && c.t === playingBeat ? 'var(--tension)' : 'none'}
                        strokeWidth={2}
                      />
                      {depth >= 3 && cellW >= 26 && (
                        <text x={c.t * cellW + cellW / 2} y={19} textAnchor="middle" className="t-rn" style={{ fontSize: 10 }}>
                          {formatRoman(c.event.spec.rn, true)}
                        </text>
                      )}
                    </g>
                  ))}
                  {FERMATAS.map((f) => (
                    <line key={f} x1={(f + 1) * cellW} x2={(f + 1) * cellW} y1={0} y2={30} className="s-ink" strokeWidth={1.2} />
                  ))}
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      <section className="panel" style={{ marginTop: 14 }} aria-live="polite">
        <div className="panel-head">
          <span className="label">
            Beat {beat + 1} · bar {barAt(passion[0].piece.measures, beat)} · melody note {melodyNote !== undefined ? pcName(midiToPitch(melodyNote)) : ''}
            {samePitch ? ' (common pitch)' : ''}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="btn" onClick={() => setBeat((b) => Math.max(0, b - 1))} aria-label="Previous beat">
              ←
            </button>
            <button className="btn" onClick={() => setBeat((b) => Math.min(BEATS - 1, b + 1))} aria-label="Next beat">
              →
            </button>
          </span>
        </div>
        <div className="beat-table">
          {selected.map(({ s, c }) => (
            <BeatRow key={s.id} s={s} c={c} depth={depth} onPlay={() => play([{ setting: s, start: c.t, end: Math.min(BEATS, c.t + 2) }])} />
          ))}
        </div>
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: 10 }}>
          The five St Matthew settings are BWV 244.15, 244.17, 244.44, 244.54 and 244.62. BWV 244.17 has the same harmony as 244.15 a semitone lower; it is derived here by
          transposition. Words are shown only where the score encodes the stanza actually sung (here, 244.54). Harmonic analyses from When in Rome; the colour is this
          site’s tension model.
        </p>
      </section>
    </div>
  );
}

function BeatRow({ s, c, depth, onPlay }: { s: Setting; c: Cell; depth: number; onPlay: () => void }) {
  return (
    <div className="beat-row">
      <button className="btn quiet" style={{ height: 30, padding: '0 8px' }} onClick={onPlay} aria-label={`Hear beat in ${s.incipit}`}>
        <PlayIcon />
      </button>
      <span className="beat-title">{s.incipit}</span>
      <span className="beat-chord">
        {depth >= 2 ? chordName(c.chord) : feelingWord(c.tension)}
        {depth >= 3 ? <span className="muted"> · {formatRoman(c.event.spec.rn)} in {keyName(c.key)}</span> : null}
      </span>
      <span className="beat-meter" aria-label={`tension ${c.tension.toFixed(2)}`}>
        <i style={{ width: `${Math.round(c.tension * 100)}%`, background: tensionColor(c.tension) }} />
      </span>
      <span className="beat-lyric">{c.lyric ? c.lyric.replace(/-$/, '–') : ''}</span>
    </div>
  );
}
